import { useEffect, useState } from 'react';
import { NJButton } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { UMR } from '../../urbanmap/_components';
import { useGeo, useProjects, useProjectStore } from './hooks';
import { sameJson } from './model';
import { ProjectList } from './ProjectList';
import { ProjectView } from './ProjectView';
import { FAMILIES, type AnySet, type Draft, type Family, type Navigation, type Project, type Tab, type TechnicalPoint } from './types';
import { ConfirmDialog } from './ui';
import { readViewerFromUrl, ViewerSwitcher, type Viewer } from './ViewerSwitcher';
import { WireframeReview } from './WireframeReview';
import { computePermissions } from './permissions';

export const meta = {
  title: 'Plan — Navigation projet',
  description: 'Hypothèses par famille, Sélections de bâtiments et Plans de développement V1 avec tracé et phasage sur carte.',
  tags: ['planning', 'list', 'poc'],
  author: 'jb6578',
  status: 'draft' as const,
  updatedAt: '2026-10-29',
};

const STUDIO_HEADER = 76; // En-tête Studio 48 + pied de page 28.

export default function NavigationProjets() {
  if (new URLSearchParams(window.location.search).get('view') === 'wireframes') return <WireframeReview />;
  return <NavigationProjetsApp />;
}

function NavigationProjetsApp() {
  const { data, isLoading, error } = useProjects();
  const { data: geo, error: geoError, refetch: reloadGeo } = useGeo();
  const store = useProjectStore(data);
  const [nav, setNav] = useState<Navigation>({ view: 'project', projectId: 'pl-gentilly', tab: 'plans' });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [planDraft, setPlanDraft] = useState<Project['plans'][number] | null>(null);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const [technicalDraft, setTechnicalDraft] = useState<TechnicalPoint[] | null>(null);
  const [viewer, setViewer] = useState<Viewer>(readViewerFromUrl);
  const canWrite = computePermissions(viewer).canWrite;

  useEffect(() => {
    if (document.querySelector(`link[href="${UMR.fontHref}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = UMR.fontHref;
    document.head.appendChild(link);
  }, []);

  const project = nav.view !== 'projects' ? store.projects.find((p) => p.id === nav.projectId) : undefined;
  const guard = (action: () => void) => (draft || planDraft || technicalDraft ? setPending(() => action) : action());
  const go = (n: Navigation) => guard(() => { setDraft(null); setPlanDraft(null); setTechnicalDraft(null); setNav(n); });

  const save = () => {
    if (!draft || !project || !canWrite) return;
    store.saveSet(project.id, draft.value);
    setDraft(null);
  };
  const savePlanDraft = () => {
    if (!planDraft || !project || !canWrite) return;
    store.updatePlan(project.id, planDraft.id, {
      buildingsConfirmed: planDraft.buildingsConfirmed,
      selectionIds: planDraft.selectionIds, selectionSources: planDraft.selectionSources,
      manualBuildingIds: planDraft.manualBuildingIds, excludedBuildingIds: planDraft.excludedBuildingIds,
      pipePhaseOverrides: planDraft.pipePhaseOverrides, technicalPoints: planDraft.technicalPoints,
      buildingIds: planDraft.buildingIds, routeMode: planDraft.routeMode,
      routePipeIndexes: planDraft.routePipeIndexes, buildingPhases: planDraft.buildingPhases, setIds: planDraft.setIds,
    });
    setPlanDraft(null);
  };
  const saveTechnical = () => {
    if (technicalDraft && project && canWrite) store.updateTechnicalPoints(project.id, technicalDraft);
    setTechnicalDraft(null);
  };

  const crumb = (label: string, onClick?: () => void) => (
    <NJButton label={label} variant="secondary" emphasis="minimal" scale="sm" disabled={!onClick} onClick={onClick} />
  );

  let body: JSX.Element;
  if (isLoading) body = <div style={{ padding: 24 }}>Chargement des projets…</div>;
  else if (error) body = <div style={{ padding: 24 }}>Erreur de chargement des projets.</div>;
  else if (!data || data.length === 0) body = <div style={{ padding: 24 }}>Aucun projet. La création de projet est hors périmètre de ce prototype.</div>;
  else if (nav.view === 'projects' || !project) {
    body = <ProjectList projects={store.projects} onOpen={(id) => setNav({ view: 'project', projectId: id, tab: 'plans' })} />;
  } else {
    const pid = project.id;
    const tab: Tab = nav.tab;
    const family = nav.family;
    const planId = nav.planId ?? project.plans[0]?.id ?? '';
    const familyMeta = family ? FAMILIES.find((f) => f.key === family) : undefined;
    const currentSet = family ? (project.sets[family] as AnySet[]).find((s) => s.id === nav.setId) : undefined;

    const base = { view: 'project' as const, projectId: pid };
    type HypoExtra = { family?: Family; setId?: string; edit?: boolean; planId?: string };
    const goHypo = (extra: HypoExtra = {}) => go({ ...base, tab: 'hypotheses', ...extra });
    const openSet = (f: Family, id: string, edit: boolean) => goHypo({ family: f, setId: id, edit });
    const mutate = (fn: () => void) => guard(() => { setDraft(null); setPlanDraft(null); fn(); });

    const current =
      tab === 'plans' ? project.plans.find((x) => x.id === planId)?.name ?? 'Plans de dev'
      : tab === 'params' ? 'Paramètres du projet'
      : tab === 'selections' ? 'Sélections'
      : tab === 'optimization' ? 'Optimisation · V2'
      : tab === 'exchange' ? 'Import-export'
      : familyMeta ? `Hypothèses › ${familyMeta.title}${currentSet ? ` › ${currentSet.name}` : ''}` : 'Hypothèses';

    body = (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <nav aria-label="Fil d’Ariane" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 16px', borderBottom: `1px solid ${UMR.border}`, fontSize: 14 }}>
          {crumb('Mes projets', () => go({ view: 'projects' }))}
          <span>/</span>
          {crumb(project.name)}
          <span>/</span>
          {crumb(current)}
          <span style={{ marginLeft: 'auto', color: UMR.textSecondary, fontSize: 12 }}>{project.perimeterLabel} · copie du {project.snapshotDate} · {project.owner}</span>
        </nav>
        <ProjectView
          project={project} geo={geo} tab={tab} family={family} setId={nav.setId ?? ''} edit={!!nav.edit} planId={planId} draft={draft} canWrite={canWrite}
          planDraft={planDraft} onPlanDraftChange={setPlanDraft}
          onTab={(t) => go({ ...base, tab: t, planId })}
          onOpenSet={openSet}
          onBackOverview={() => goHypo({})}
          onSelectFamily={(f) => goHypo({ family: f })}
          onSelectSet={(id) => goHypo({ family, setId: id, edit: false })}
          onModify={() => goHypo({ family, setId: nav.setId, edit: true })}
          onConsult={() => goHypo({ family, setId: nav.setId, edit: false })}
          onCreateSet={(f) => mutate(() => setNav({ ...base, tab: 'hypotheses', family: f, setId: store.createSet(pid, f), edit: true }))}
          onDuplicateSet={(f, id) => mutate(() => setNav({ ...base, tab: 'hypotheses', family: f, setId: store.duplicateSet(pid, f, id), edit: true }))}
          onDeleteSet={(f, id) => mutate(() => { store.deleteSet(pid, f, id); setNav({ ...base, tab: 'hypotheses', family: f }); })}
          onSetChange={(v: AnySet) => {
            const orig = family ? (project.sets[family] as AnySet[]).find((x) => x.id === v.id) : undefined;
            setDraft(orig && sameJson(orig, v) ? null : { kind: 'set', value: v });
          }}
          onSave={save} onCancel={() => setDraft(null)}
          onSelectPlan={(id) => go({ ...base, tab: 'plans', planId: id })}
          onRun={(id, section) => { if (canWrite) void store.run(pid, id, section, geo); }}
          technicalDraft={technicalDraft} onTechnicalDraft={setTechnicalDraft} onConfirmTechnical={saveTechnical}
          onCreatePlan={(name, energy) => store.createPlan(pid, name, energy)}
          onDuplicatePlan={(id) => store.duplicatePlan(pid, id)}
          onUpdatePlan={(id, changes) => store.updatePlan(pid, id, changes)}
          onCreateSelection={(name, buildingIds) => store.createSelection(pid, name, buildingIds)}
          onUpdateSelection={(id, changes) => store.updateSelection(pid, id, changes)}
          onDeleteSelection={(id) => store.deleteSelection(pid, id)}
        />
      </div>
    );
  }

  const pendingDialog = pending;
  return (
    <PrototypeStudioLayout title={meta.title}>
      <div style={{ position: 'relative', height: `calc(100vh - ${STUDIO_HEADER}px)`, overflow: 'hidden', fontFamily: UMR.font, background: UMR.white, color: UMR.black }}>
        {body}
        {geoError && <div role="alert" style={{ position: 'absolute', top: 50, right: 12, padding: 12, background: UMR.white }}>
          Géométries indisponibles.
          <NJButton label="Recharger la carte" variant="secondary" scale="sm" onClick={() => { void reloadGeo(); }} />
        </div>}
        {store.mutationError && <div role="alert" style={{ position: 'absolute', bottom: 8, left: 120, padding: 12, background: UMR.white }}>{store.mutationError}</div>}
        {pendingDialog && (
          <ConfirmDialog
            title="Modifications non confirmées"
            onStay={() => setPending(null)}
            onDiscard={() => { const a = pendingDialog; setDraft(null); setPlanDraft(null); setTechnicalDraft(null); setPending(null); a(); }}
            onSave={() => { const a = pendingDialog; save(); savePlanDraft(); saveTechnical(); setPending(null); a(); }}
          >
            Des modifications du plan, des hypothèses ou des SST ne sont pas confirmées. Que souhaitez-vous faire ?
          </ConfirmDialog>
        )}
      </div>
      <ViewerSwitcher viewer={viewer} onChange={(next) => {
        const url = new URL(window.location.href);
        url.searchParams.set('as', next);
        window.history.replaceState({}, '', url);
        setViewer(next);
        if (next === 'read') {
          setDraft(null);
          setPlanDraft(null);
          setTechnicalDraft(null);
          setNav((current) => ({ ...current, edit: false }));
        }
      }} />
    </PrototypeStudioLayout>
  );
}
